import html2canvas from "html2canvas";
import {
  applyBackgroundStyles,
  getHtml2CanvasBackgroundColor,
} from "../ProjectV2/utils/projectBackground";

const RENDER_SCALE = 2; // render 2× for sharpness, then downscale back
const TARGET_SCALE_EPSILON = 1e-7;

/**
 * Card role/name text must be rasterized at a whole-number scale. Fractional
 * capture scales can round mixed font sizes onto different pixel rows, making
 * otherwise identical browser baselines appear offset in the exported frame.
 * Capture at least as large as the requested output, then resize once.
 */
export function getCardTargetAlignedCaptureScale({
  captureHeight,
  targetHeight,
  fallbackScale = RENDER_SCALE,
}) {
  const sourceHeight = Number(captureHeight);
  const outputHeight = Number(targetHeight);
  if (!(sourceHeight > 0) || !(outputHeight > 0)) return fallbackScale;

  const minimumScale = Math.max(Number(fallbackScale) || RENDER_SCALE, 1);
  const outputScale = Math.ceil(
    outputHeight / sourceHeight - TARGET_SCALE_EPSILON,
  );

  return Math.max(minimumScale, outputScale);
}

/**
 * Scroll blocks keep the editor's canonical layout width so text wrapping is
 * identical to the preview. Rasterize that layout at least as wide as the
 * requested video so the final PNG is never enlarged after text and
 * backgrounds have already been converted to pixels.
 */
export function getScrollTargetAlignedCaptureScale({
  captureWidth,
  targetWidth,
  fallbackScale = RENDER_SCALE,
}) {
  const sourceWidth = Number(captureWidth);
  const outputWidth = Number(targetWidth);
  if (!(sourceWidth > 0) || !(outputWidth > 0)) return fallbackScale;

  return Math.max(
    fallbackScale,
    outputWidth / sourceWidth + TARGET_SCALE_EPSILON,
  );
}

/**
 * Downscales (or resizes) a canvas to destWidth × destHeight using OffscreenCanvas.
 */
async function resizeCanvasToBlob(sourceCanvas, destWidth, destHeight) {
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(sourceCanvas, 0, 0, destWidth, destHeight);
  return offscreen.convertToBlob({ type: "image/png" });
}

export function getLayoutPreservedHeight({
  capturedHeight,
  layoutHeight,
  layoutWidth,
  targetWidth,
}) {
  const captured = Math.max(1, Math.round(Number(capturedHeight) || 0));
  const sourceHeight = Number(layoutHeight);
  const sourceWidth = Number(layoutWidth);
  const outputWidth = Number(targetWidth);

  if (!sourceHeight || !sourceWidth || !outputWidth) return captured;

  return Math.max(
    captured,
    Math.max(1, Math.round(sourceHeight * (outputWidth / sourceWidth))),
  );
}

export function getCumulativeScaledBlockHeight({
  previousLayoutBottom,
  layoutBottom,
  layoutWidth,
  targetWidth,
}) {
  const sourceWidth = Number(layoutWidth);
  const outputWidth = Number(targetWidth);
  if (!sourceWidth || !outputWidth) return 1;

  const scale = outputWidth / sourceWidth;
  const previous = Math.round((Number(previousLayoutBottom) || 0) * scale);
  const next = Math.round((Number(layoutBottom) || 0) * scale);
  return Math.max(1, next - previous);
}

function isBulletFlexLayout(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.matches(".bullet") || el.classList.contains("bullet")) return true;
  if (el.parentElement?.querySelector(":scope > .bullet")) return true;
  const wrap = el.closest(".flex-wrap");
  return Boolean(wrap && wrap.querySelector(".bullet"));
}

function isNameOnlyCreditName(el) {
  // NameOnly2 grid: .overlaytext[display=grid] > .flex.flex-col > p
  // Freezing those single-line names is unnecessary and can skew capture height.
  const col = el.parentElement;
  const grid = col?.parentElement;
  if (!grid?.classList?.contains("overlaytext")) return false;
  try {
    return getComputedStyle(grid).display === "grid";
  } catch {
    return false;
  }
}

function isLeafTextContainer(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.closest("[data-html2canvas-ignore]")) return false;
  if (isBulletFlexLayout(el)) return false;
  if (isNameOnlyCreditName(el)) return false;
  if (el.matches("img, svg, canvas, video, br, script, style")) return false;
  if (el.querySelector("img, svg, canvas, video, p, div, h1, h2, h3, h4, li, table, tr, td")) {
    return false;
  }
  const text = el.textContent;
  return Boolean(text && text.trim());
}

/**
 * Read the live (browser) line breaks of an element by walking text-node
 * glyphs and watching getClientRects() Y changes. This is the preview wrap.
 */
export function getWrappedTextLines(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const lines = [];
  let current = "";
  let lastTop = null;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const value = node.nodeValue;
    if (!value) continue;

    for (let i = 0; i < value.length; i += 1) {
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const rect = range.getClientRects()[0];
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        current += value[i];
        continue;
      }

      if (
        lastTop !== null &&
        Math.abs(rect.top - lastTop) > Math.max(2, rect.height * 0.35)
      ) {
        lines.push(current);
        current = value[i];
      } else {
        current += value[i];
      }
      lastTop = rect.top;
    }
  }

  if (current !== "" || lines.length === 0) lines.push(current);

  let tail = el.lastChild;
  while (tail && tail.nodeType === 3 && !String(tail.nodeValue || "").trim()) {
    tail = tail.previousSibling;
  }
  if (tail && tail.nodeName === "BR") lines.push("");

  return lines;
}

function applyFrozenLines(el, lines, lockSpaces = false) {
  const fragment = document.createDocumentFragment();
  lines.forEach((line, i) => {
    if (i > 0) fragment.appendChild(document.createElement("br"));
    const span = document.createElement("span");
    span.setAttribute("data-frozen-line", "true");
    span.style.whiteSpace = "nowrap";
    span.style.wordBreak = "keep-all";
    span.style.overflowWrap = "normal";
    span.style.wordWrap = "normal";
    span.style.display = lockSpaces ? "inline" : "inline-block";
    span.textContent = line;
    fragment.appendChild(span);
  });
  el.replaceChildren(fragment);
}

/**
 * Snapshot live wrapping on every leaf text node, then replace it with
 * explicit nowrap lines + <br>. html2canvas re-lays out text with its own
 * glyph metrics; without this, a word like "Mark" can jump up a line.
 */
export function freezePreviewWrapping(root, liveSnapshots = null) {
  if (!root) return () => {};

  // Freeze wrapping <p> credit lines (song title/details, blurbs).
  // Name-only paragraph bullets live in a flex-wrap + CSS grid; rewriting
  // those nodes stacks every name on top of each other in html2canvas.
  // Do not freeze role/name spans — rewriting those table cells stacks
  // role and name on top of each other in the export.
  const candidates = [...root.querySelectorAll("p")].filter(isLeafTextContainer);

  const snapshots = candidates.map((el, index) => {
    const live = liveSnapshots?.[index];
    const lines = live?.lines?.length ? live.lines : getWrappedTextLines(el);
    return {
      el,
      html: el.innerHTML,
      whiteSpace: el.style.whiteSpace,
      styleWidth: el.style.width,
      maxWidth: el.style.maxWidth,
      boxSizing: el.style.boxSizing,
      flexShrink: el.style.flexShrink,
      position: el.style.position,
      top: el.style.top,
      captureWidth: live?.width,
      canvasBaselineShift: live?.canvasBaselineShift ?? 0,
      lines,
    };
  });

  snapshots.forEach((snap) => {
    // Use layout width (offsetWidth), not getBoundingClientRect — export
    // applies transform: scale(videoWidth/800) on the preview content, and
    // getBoundingClientRect includes that scale. Freezing the scaled px width
    // into an unscaled layout box overflows the flex row and shifts blurbs
    // (and other centered <p> blocks) off-center in the PNG.
    const liveWidth = snap.captureWidth || snap.el.offsetWidth;
    const inSongColumn = Boolean(snap.el.closest(".songColumnsRow"));
    if (liveWidth > 0) {
      snap.el.style.boxSizing = "border-box";
      snap.el.style.maxWidth = `${liveWidth}px`;
      if (inSongColumn) {
        snap.el.style.width = "100%";
      } else {
        snap.el.style.width = `${liveWidth}px`;
        snap.el.style.flexShrink = "0";
      }
    }
    applyFrozenLines(snap.el, snap.lines, true);
    const baselineShift = Number(snap.canvasBaselineShift);
    if (Number.isFinite(baselineShift) && baselineShift !== 0) {
      snap.el.style.position = "relative";
      snap.el.style.top = `${baselineShift}px`;
    }
  });

  return () => {
    snapshots.forEach((snap) => {
      snap.el.innerHTML = snap.html;
      snap.el.style.whiteSpace = snap.whiteSpace;
      snap.el.style.width = snap.styleWidth;
      snap.el.style.maxWidth = snap.maxWidth;
      snap.el.style.boxSizing = snap.boxSizing;
      snap.el.style.flexShrink = snap.flexShrink;
      snap.el.style.position = snap.position;
      snap.el.style.top = snap.top;
    });
  };
}

function assignStyles(el, styles) {
  Object.keys(styles).forEach((key) => {
    el.style[key] = styles[key];
  });
}

function collectRoleNameSpans(root) {
  const pairedSpans = [...root.querySelectorAll(".roleName_td > span")];
  const raggedExtraSpans = [
    ...root.querySelectorAll(".roleName-ragged-extra-name > span"),
  ];
  const lineSpans = [...root.querySelectorAll("span.roleName_line")].filter(
    (span) => !span.closest("td.roleName_td"),
  );
  return [...new Set([...pairedSpans, ...raggedExtraSpans, ...lineSpans])].filter(
    (span) => span.textContent && span.textContent.replace(/\s/g, ""),
  );
}

const HTML2CANVAS_BASELINE_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const html2CanvasBaselineCache = new Map();

export function getCardRoleNameCanvasBaselineShift(
  browserBaseline,
  textTop,
  html2CanvasBaseline,
) {
  if (
    browserBaseline == null ||
    textTop == null ||
    html2CanvasBaseline == null
  ) {
    return 0;
  }
  const desired = Number(browserBaseline);
  const rangeTop = Number(textTop);
  const canvasBaseline = Number(html2CanvasBaseline);
  if (
    !Number.isFinite(desired) ||
    !Number.isFinite(rangeTop) ||
    !Number.isFinite(canvasBaseline)
  ) {
    return 0;
  }
  return desired - (rangeTop + canvasBaseline);
}

/**
 * Position a capture overlay from the geometry of the final DOM that
 * html2canvas will rasterize. Freezing lines adds wrapper spans and can move
 * the text range relative to the overlay box, so a correction measured from
 * the live source span is only a fallback.
 */
export function getRoleNameOverlayTop({
  initialTop,
  targetBaselineInRoot,
  captureTextTopInRoot,
  html2CanvasBaseline,
  fallbackShift = 0,
}) {
  const top = Number(initialTop);
  const target = Number(targetBaselineInRoot);
  const textTop = Number(captureTextTopInRoot);
  const canvasBaseline = Number(html2CanvasBaseline);

  if (
    targetBaselineInRoot != null &&
    captureTextTopInRoot != null &&
    html2CanvasBaseline != null &&
    Number.isFinite(target) &&
    Number.isFinite(textTop) &&
    Number.isFinite(canvasBaseline)
  ) {
    return top + target - (textTop + canvasBaseline);
  }

  const fallback = Number(fallbackShift);
  return top + (Number.isFinite(fallback) ? fallback : 0);
}

function validVisualScale(visualSize, layoutSize, fallback = 1) {
  const visual = Number(visualSize);
  const layout = Number(layoutSize);
  const scale = layout > 0 ? visual / layout : fallback;
  return Number.isFinite(scale) && scale > 0 ? scale : fallback;
}

/**
 * Convert live preview measurements back to canonical layout pixels. The
 * editor fits the 800px card with CSS zoom, while the isolated render clone is
 * always unzoomed. Mixing those two coordinate systems shifts differently
 * sized Role and Name fonts by different amounts.
 */
export function getCardRoleNameCaptureGeometry({
  rootRect,
  rootOffsetWidth,
  rootOffsetHeight,
  spanRect,
  spanScrollWidth = 0,
  browserBaseline,
  textTop,
  html2CanvasBaseline,
}) {
  const scaleX = validVisualScale(rootRect?.width, rootOffsetWidth);
  const scaleY = validVisualScale(
    rootRect?.height,
    rootOffsetHeight,
    scaleX,
  );
  const spanTop = Number(spanRect?.top) || 0;
  const normalizedBrowserBaseline =
    browserBaseline == null
      ? null
      : (Number(browserBaseline) - spanTop) / scaleY;
  const normalizedTextTop =
    textTop == null ? null : (Number(textTop) - spanTop) / scaleY;
  const normalizedWidth = (Number(spanRect?.width) || 0) / scaleX;

  return {
    width: Math.max(normalizedWidth, Number(spanScrollWidth) || 0),
    canvasBaselineShift: getCardRoleNameCanvasBaselineShift(
      normalizedBrowserBaseline,
      normalizedTextTop,
      html2CanvasBaseline,
    ),
    captureBox: {
      left:
        ((Number(spanRect?.left) || 0) - (Number(rootRect?.left) || 0)) /
        scaleX,
      top:
        ((Number(spanRect?.top) || 0) - (Number(rootRect?.top) || 0)) /
        scaleY,
      width: normalizedWidth,
      height: (Number(spanRect?.height) || 0) / scaleY,
    },
    scaleX,
    scaleY,
  };
}

function getLastTextRectTop(el) {
  const ownerDocument = el?.ownerDocument || document;
  const showText = ownerDocument.defaultView?.NodeFilter?.SHOW_TEXT ?? 4;
  const walker = ownerDocument.createTreeWalker(el, showText);
  const range = ownerDocument.createRange();
  if (typeof range.getClientRects !== "function") return null;
  let lastTop = null;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!String(node.nodeValue || "").trim()) continue;
    range.selectNodeContents(node);
    const rects = range.getClientRects();
    if (rects.length) lastTop = rects[rects.length - 1].top;
  }

  range.detach?.();
  return lastTop;
}

function getBrowserAlphabeticBaseline(el) {
  const ownerDocument = el?.ownerDocument || document;
  const marker = ownerDocument.createElement("span");
  marker.setAttribute("data-card-baseline-marker", "true");
  marker.style.cssText = [
    "display:inline-block",
    "width:0",
    "height:0",
    "margin:0",
    "padding:0",
    "border:0",
    "font-size:0",
    "line-height:0",
    "vertical-align:baseline",
  ].join(";");

  const children = [...el.childNodes];
  const trailingBreak = [...children]
    .reverse()
    .find((node) => node.nodeType === 1 || String(node.nodeValue || "").trim());
  if (trailingBreak?.nodeName === "BR") {
    el.insertBefore(marker, trailingBreak);
  } else {
    el.appendChild(marker);
  }
  const baseline = marker.getBoundingClientRect().top;
  marker.remove();
  return baseline;
}

/**
 * Measure the browser's alphabetic baseline relative to its text range
 * without letting the temporary zero-width marker create a new wrapped line.
 * Intrinsic-width ragged Role/Name cells are deliberately only as wide as
 * their text; appending even a zero-width inline marker at that boundary can
 * wrap the marker and make the measured baseline one whole row too low.
 *
 * The relative offset is independent of the element's page position. Taking
 * both measurements while wrapping is disabled also keeps naturally wrapped
 * blocks safe: after the original white-space rule is restored, the same
 * font-relative offset applies to their actual final line.
 */
function getBrowserAlphabeticBaselineOffset(el) {
  const previousWhiteSpace = el.style.whiteSpace;
  el.style.whiteSpace = "nowrap";

  try {
    const baseline = getBrowserAlphabeticBaseline(el);
    const textTop = getLastTextRectTop(el);
    if (textTop == null) return null;
    return baseline - textTop;
  } finally {
    el.style.whiteSpace = previousWhiteSpace;
  }
}

function textNodePaintContainers(root) {
  if (!root) return [];
  const ownerDocument = root.ownerDocument || document;
  const ownerWindow = ownerDocument.defaultView || window;
  const showText = ownerWindow.NodeFilter?.SHOW_TEXT ?? 4;
  const walker = ownerDocument.createTreeWalker(root, showText);
  const containers = new Set();

  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!String(node.nodeValue || "").trim()) continue;
    const el = node.parentElement;
    if (!el || !root.contains(el)) continue;
    if (el.closest("[data-html2canvas-ignore], script, style")) continue;
    const computed = ownerWindow.getComputedStyle(el);
    if (
      computed.display === "none" ||
      computed.visibility === "hidden" ||
      parseFloat(computed.opacity) === 0
    ) {
      continue;
    }
    containers.add(el);
  }

  return [...containers];
}

function addCanvasPaintOffset(el, offsetY) {
  const shift = Number(offsetY);
  if (!Number.isFinite(shift) || Math.abs(shift) < 0.001) return;

  const ownerWindow = el.ownerDocument?.defaultView || window;
  const computed = ownerWindow.getComputedStyle(el);
  const position = computed.position;
  const currentTop = parseFloat(computed.top);

  if (position === "absolute" || position === "fixed" || position === "sticky") {
    if (Number.isFinite(currentTop)) {
      el.style.top = `${currentTop + shift}px`;
      return;
    }
    const transform = computed.transform === "none" ? "" : computed.transform;
    el.style.transform = `${transform} translateY(${shift}px)`.trim();
    return;
  }

  if (position === "static") el.style.position = "relative";
  el.style.top = `${(Number.isFinite(currentTop) ? currentTop : 0) + shift}px`;
}

/**
 * html2canvas paints text from Range.getClientRects() plus its own integer
 * font baseline. Run inside html2canvas's final iframe clone so the browser
 * line box and the canvas paint baseline are measured from the exact same DOM.
 * This is intentionally generic: every visible text node uses one parity rule
 * instead of accumulating separate Role/Name, heading, and Name Only nudges.
 */
export function calibrateFinalCloneTextBaselines(root) {
  const containers = textNodePaintContainers(root);

  containers.forEach((el) => {
    const ownerWindow = el.ownerDocument?.defaultView || window;
    const computed = ownerWindow.getComputedStyle(el);
    const browserBaselineOffset = getBrowserAlphabeticBaselineOffset(el);
    const canvasBaseline = getHtml2CanvasAlphabeticBaseline(
      computed.fontFamily,
      computed.fontSize,
    );
    const shift = getCardRoleNameCanvasBaselineShift(
      browserBaselineOffset,
      0,
      canvasBaseline,
    );
    addCanvasPaintOffset(el, shift);
  });

  return containers.length;
}

// Mirror html2canvas 1.4.1's FontMetrics.parseMetrics. Its text
// renderer ignores the browser's table-cell baseline and paints from this
// independently measured alphabetic baseline instead.
function getHtml2CanvasAlphabeticBaseline(fontFamily, fontSize) {
  const key = `${fontFamily}|${fontSize}`;
  if (html2CanvasBaselineCache.has(key)) {
    return html2CanvasBaselineCache.get(key);
  }

  const container = document.createElement("div");
  const image = document.createElement("img");
  const sample = document.createElement("span");

  container.style.visibility = "hidden";
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "0";
  container.style.fontFamily = fontFamily;
  container.style.fontSize = fontSize;
  container.style.margin = "0";
  container.style.padding = "0";
  container.style.whiteSpace = "nowrap";

  image.src = HTML2CANVAS_BASELINE_IMAGE;
  image.width = 1;
  image.height = 1;
  image.style.margin = "0";
  image.style.padding = "0";
  image.style.verticalAlign = "baseline";

  sample.style.fontFamily = fontFamily;
  sample.style.fontSize = fontSize;
  sample.style.margin = "0";
  sample.style.padding = "0";
  sample.appendChild(document.createTextNode("Hidden Text"));

  container.appendChild(sample);
  container.appendChild(image);
  document.body.appendChild(container);
  const baseline = image.offsetTop - sample.offsetTop + 2;
  container.remove();

  html2CanvasBaselineCache.set(key, baseline);
  return baseline;
}

/**
 * Paragraph blocks are captured inside a height fixed from the live Scroll
 * preview. Preserve the live line breaks here; paint-baseline parity is
 * applied later inside html2canvas's own final clone.
 */
function snapshotFrozenTextWraps(root) {
  if (!root) return [];

  return [...root.querySelectorAll("p")]
    .filter(isLeafTextContainer)
    .map((el) => ({
      lines: getWrappedTextLines(el),
      width: el.offsetWidth,
    }));
}

/** Preview wrap is the source of truth — never re-measure on the clone. */
function snapshotRoleNameWraps(root) {
  const rootRect = root.getBoundingClientRect();
  const rootOffsetWidth = root.offsetWidth;
  const rootOffsetHeight = root.offsetHeight;
  return collectRoleNameSpans(root).map((span) => {
    const td = span.closest("td.roleName_td");
    const tdStyle = td ? window.getComputedStyle(td) : null;
    const spanRect = span.getBoundingClientRect();
    const padTop = parseFloat(tdStyle?.paddingTop) || 0;
    const spanStyle = window.getComputedStyle(span);
    const browserBaseline = getBrowserAlphabeticBaseline(span);
    const textTop = getLastTextRectTop(span);
    const html2CanvasBaseline = getHtml2CanvasAlphabeticBaseline(
      spanStyle.fontFamily,
      spanStyle.fontSize,
    );
    const geometry = getCardRoleNameCaptureGeometry({
      rootRect,
      rootOffsetWidth,
      rootOffsetHeight,
      spanRect,
      spanScrollWidth: span.scrollWidth,
      browserBaseline,
      textTop,
      html2CanvasBaseline,
    });
    const tdTop = td?.getBoundingClientRect?.().top;
    const normalizedTopInCell =
      tdTop == null ? 0 : (spanRect.top - tdTop) / geometry.scaleY;
    const normalizedBaselineInCell =
      tdTop == null
        ? null
        : (browserBaseline - tdTop) / geometry.scaleY;
    const normalizedBaselineInRoot =
      browserBaseline == null
        ? null
        : (browserBaseline - rootRect.top) / geometry.scaleY;

    return {
      lines: getWrappedTextLines(span),
      width: geometry.width,
      baselineShift: Math.max(0, normalizedTopInCell - padTop),
      baselineInCell: normalizedBaselineInCell,
      baselineInRoot: normalizedBaselineInRoot,
      // Calibrate this exact font/size in canonical layout pixels. This keeps
      // the correction identical at every editor/browser display zoom.
      canvasBaselineShift: geometry.canvasBaselineShift,
      captureBox: geometry.captureBox,
    };
  });
}

function boxOffsetInTd(span, td) {
  const s = span.getBoundingClientRect();
  const t = td.getBoundingClientRect();
  return {
    top: s.top - t.top,
    left: s.left - t.left,
  };
}

/**
 * Scroll keeps role/name text in-flow, so reproduce both parts of the live
 * preview position: the table-cell baseline offset and html2canvas's
 * font-specific paint correction. The correction is signed because either
 * Role or Name may be the larger text.
 */
export function getScrollRoleNameCaptureTop(
  previewBaselineShift,
  canvasBaselineShift,
  targetBaselineInCell = null,
  captureTextTopInCell = null,
  html2CanvasBaseline = null,
) {
  const target = Number(targetBaselineInCell);
  const textTop = Number(captureTextTopInCell);
  const canvasBaseline = Number(html2CanvasBaseline);
  if (
    targetBaselineInCell != null &&
    captureTextTopInCell != null &&
    html2CanvasBaseline != null &&
    Number.isFinite(target) &&
    Number.isFinite(textTop) &&
    Number.isFinite(canvasBaseline)
  ) {
    return target - (textTop + canvasBaseline);
  }

  const preview = Number(previewBaselineShift);
  const canvas = Number(canvasBaselineShift);
  return (
    (Number.isFinite(preview) ? preview : 0) +
    (Number.isFinite(canvas) ? canvas : 0)
  );
}

/**
 * Keep role/name in-flow (so reduced width cannot collapse cells) and use the
 * vertical offsets measured from the preview. The capture cells are top
 * aligned deliberately: asking html2canvas to baseline-align them again after
 * applying those offsets double-adjusts differently-sized Role and Name text.
 * Shift the in-flow span itself with relative positioning; html2canvas can
 * drop vertical padding on block spans inside table cells, while relative top
 * is reflected directly in the painted box.
 * Absolute text made html2canvas drop table min-content width and the pairs
 * collapsed again.
 */
export function freezeRoleNameToPreview(root, liveSnap = null) {
  if (!root) return () => {};

  const liveBySpan = liveSnap
    ? new Map(
        collectRoleNameSpans(root).map((span, index) => [
          span,
          liveSnap[index] || null,
        ]),
      )
    : null;
  const takeLive = (span) => liveBySpan?.get(span) || null;

  const jobs = [];
  root.querySelectorAll("table.roleName_tr").forEach((table) => {
    const cells = [...table.querySelectorAll("td.roleName_td")]
      .map((td) => {
        const span = [...td.children].find((el) => el.tagName === "SPAN");
        if (!span || !span.textContent.replace(/\s/g, "")) return null;

        const live = takeLive(span);
        const { top } = boxOffsetInTd(span, td);
        const cs = window.getComputedStyle(td);
        const padTop = parseFloat(cs.paddingTop) || 0;

        return {
          td,
          span,
          tdWidth: td.getBoundingClientRect().width || td.offsetWidth,
          tdHeight: td.offsetHeight,
          spanWidth: live?.width || Math.max(span.offsetWidth, span.scrollWidth),
          baselineShift:
            live?.baselineShift ?? Math.max(0, top - padTop),
          baselineInCell: live?.baselineInCell ?? null,
          canvasBaselineShift: live?.canvasBaselineShift ?? 0,
          textAlign: cs.textAlign,
          lines: live?.lines?.length ? live.lines : getWrappedTextLines(span),
          html: span.innerHTML,
          tdStyle: {
            position: td.style.position,
            width: td.style.width,
            height: td.style.height,
            minWidth: td.style.minWidth,
            maxWidth: td.style.maxWidth,
            boxSizing: td.style.boxSizing,
            verticalAlign: td.style.verticalAlign,
            overflow: td.style.overflow,
            whiteSpace: td.style.whiteSpace,
            wordBreak: td.style.wordBreak,
            overflowWrap: td.style.overflowWrap,
          },
          spanStyle: {
            position: span.style.position,
            top: span.style.top,
            left: span.style.left,
            display: span.style.display,
            width: span.style.width,
            height: span.style.height,
            maxWidth: span.style.maxWidth,
            minWidth: span.style.minWidth,
            margin: span.style.margin,
            paddingTop: span.style.paddingTop,
            whiteSpace: span.style.whiteSpace,
            wordBreak: span.style.wordBreak,
            overflowWrap: span.style.overflowWrap,
            textAlign: span.style.textAlign,
            verticalAlign: span.style.verticalAlign,
          },
        };
      })
      .filter(Boolean);

    if (!cells.length) return;
    jobs.push({
      table,
      tableWidth: table.getBoundingClientRect().width || table.offsetWidth,
      tableHeight: table.offsetHeight,
      tableStyle: {
        position: table.style.position,
        width: table.style.width,
        height: table.style.height,
        minWidth: table.style.minWidth,
        maxWidth: table.style.maxWidth,
        tableLayout: table.style.tableLayout,
      },
      cells,
    });
  });

  jobs.forEach(({ table, tableWidth, tableStyle, cells }) => {
    const fixedLayout = tableStyle.tableLayout === "fixed";
    table.style.width = `${tableWidth}px`;
    table.style.minWidth = `${tableWidth}px`;
    table.style.height = "auto";
    table.style.marginBottom = "0";
    table.style.borderSpacing = "0";
    table.style.tableLayout = tableStyle.tableLayout || "auto";
    cells.forEach((cell) => {
      const lines = (cell.lines || []).filter(
        (line, i, arr) => String(line).replace(/\s/g, "") !== "" || arr.length === 1,
      );
      const singleLine = lines.length <= 1;
      cell.td.style.position = "static";
      cell.td.style.boxSizing = "border-box";
      if (!fixedLayout) {
        cell.td.style.width = `${cell.tdWidth}px`;
        cell.td.style.minWidth = `${cell.tdWidth}px`;
        cell.td.style.maxWidth = `${cell.tdWidth}px`;
      }
      cell.td.style.height = "auto";
      // The relative top below reproduces the preview offset plus the saved
      // html2canvas font correction. Keep the cell at the row top so the table
      // cannot apply a second, differently rounded baseline during capture.
      cell.td.style.verticalAlign = "top";
      cell.td.style.overflow = "visible";
      cell.td.style.wordBreak = "keep-all";
      cell.td.style.overflowWrap = "normal";
      cell.td.style.whiteSpace = singleLine ? "nowrap" : "normal";
      applyFrozenLines(cell.span, lines, true);
      cell.span.style.position = "relative";
      cell.span.style.top = "0px";
      cell.span.style.left = "auto";
      cell.span.style.margin = "0";
      cell.span.style.paddingTop = "0";
      cell.span.style.height = "auto";
      cell.span.style.maxWidth = fixedLayout ? "100%" : "none";
      cell.span.style.width = fixedLayout ? cell.spanStyle.width : "auto";
      cell.span.style.whiteSpace = singleLine ? "nowrap" : "normal";
      cell.span.style.wordBreak = "keep-all";
      cell.span.style.overflowWrap = "normal";
      cell.span.style.wordWrap = "normal";
      cell.span.style.verticalAlign = "baseline";
      cell.span.style.textAlign = cell.textAlign;
      cell.span.style.display = cell.spanStyle.display || "inline-block";
      if (!fixedLayout && cell.spanWidth > 0) {
        cell.span.style.minWidth = `${cell.spanWidth}px`;
      }

      // Frozen line wrappers can move the Range text box by a fraction of a
      // pixel. Calibrate from that final DOM instead of reusing its pre-freeze
      // text top, while targeting the exact baseline saved from the preview.
      const captureTextTop = getLastTextRectTop(cell.span);
      const captureCellTop = cell.td.getBoundingClientRect().top;
      const captureStyle = window.getComputedStyle(cell.span);
      const captureCanvasBaseline = getHtml2CanvasAlphabeticBaseline(
        captureStyle.fontFamily,
        captureStyle.fontSize,
      );
      cell.span.style.top = `${getScrollRoleNameCaptureTop(
        cell.baselineShift,
        cell.canvasBaselineShift,
        cell.baselineInCell,
        captureTextTop == null ? null : captureTextTop - captureCellTop,
        captureCanvasBaseline,
      )}px`;
    });
  });

  const lineJobs = [];
  root.querySelectorAll("span.roleName_line").forEach((span) => {
    if (span.closest("td.roleName_td")) return;
    if (!span.textContent.replace(/\s/g, "")) return;
    const live = takeLive(span);
    const lines = live?.lines?.length ? live.lines : getWrappedTextLines(span);
    lineJobs.push({
      span,
      spanWidth: live?.width || Math.max(span.offsetWidth, span.scrollWidth),
      lines,
      html: span.innerHTML,
      spanStyle: {
        whiteSpace: span.style.whiteSpace,
        wordBreak: span.style.wordBreak,
        overflowWrap: span.style.overflowWrap,
        wordWrap: span.style.wordWrap,
        maxWidth: span.style.maxWidth,
        minWidth: span.style.minWidth,
        display: span.style.display,
      },
    });
  });
  lineJobs.forEach((job) => {
    applyFrozenLines(job.span, job.lines, true);
    job.span.style.whiteSpace = "nowrap";
    job.span.style.wordBreak = "keep-all";
    job.span.style.overflowWrap = "normal";
    job.span.style.wordWrap = "normal";
    job.span.style.maxWidth = "none";
    job.span.style.display = "inline-block";
    if (job.spanWidth > 0) job.span.style.minWidth = `${job.spanWidth}px`;
  });

  return () => {
    jobs.forEach(({ table, tableStyle, cells }) => {
      assignStyles(table, tableStyle);
      cells.forEach((cell) => {
        assignStyles(cell.td, cell.tdStyle);
        assignStyles(cell.span, cell.spanStyle);
        cell.span.innerHTML = cell.html;
      });
    });
    lineJobs.forEach(({ span, spanStyle, html }) => {
      assignStyles(span, spanStyle);
      span.innerHTML = html;
    });
  };
}

const ROLE_NAME_OVERLAY_STYLE_PROPS = [
  "color",
  "direction",
  "font-family",
  "font-feature-settings",
  "font-kerning",
  "font-size",
  "font-stretch",
  "font-style",
  "font-variant",
  "font-variation-settings",
  "font-weight",
  "letter-spacing",
  "line-height",
  "opacity",
  "text-align",
  "text-decoration-color",
  "text-decoration-line",
  "text-decoration-style",
  "text-decoration-thickness",
  "text-rendering",
  "text-shadow",
  "text-transform",
  "-webkit-font-smoothing",
  "-webkit-text-stroke-color",
  "-webkit-text-stroke-width",
  "word-spacing",
];

/**
 * html2canvas does not preserve the browser's glyph baseline for differently
 * sized text inside table cells. Card frames have a fixed preview box, so use
 * the live browser boxes as the source of truth: keep the original cells in
 * flow (hidden, so all surrounding layout stays unchanged) and paint copies
 * at their exact preview coordinates outside the table formatting context.
 * Scroll and Card captures both use the live browser boxes as their source of
 * truth. Keeping the originals in flow preserves each project's layout while
 * the calibrated overlays make html2canvas paint on those exact baselines.
 */
export function overlayRoleNameAtPreviewPositions(root, liveSnap = null) {
  if (!root || !liveSnap?.length) return () => {};

  const spans = collectRoleNameSpans(root);
  const previousRootPosition = root.style.position;
  const originals = [];
  const overlays = [];

  root.style.position = "relative";

  spans.forEach((span, index) => {
    const snap = liveSnap[index];
    const box = snap?.captureBox;
    if (!box || !Number.isFinite(box.left) || !Number.isFinite(box.top)) return;

    const computed = window.getComputedStyle(span);
    const overlay = span.cloneNode(true);
    applyFrozenLines(
      overlay,
      snap.lines?.length ? snap.lines : getWrappedTextLines(span),
      true,
    );

    ROLE_NAME_OVERLAY_STYLE_PROPS.forEach((prop) => {
      const value = computed.getPropertyValue(prop);
      if (value) overlay.style.setProperty(prop, value);
    });

    overlay.setAttribute("data-card-role-name-overlay", "true");
    assignStyles(overlay, {
      position: "absolute",
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      minWidth: "0",
      maxWidth: "none",
      margin: "0",
      padding: "0",
      border: "0",
      boxSizing: "border-box",
      display: "block",
      overflow: "visible",
      visibility: "visible",
      whiteSpace: snap.lines?.length > 1 ? "normal" : "nowrap",
      wordBreak: "keep-all",
      overflowWrap: "normal",
      wordWrap: "normal",
      pointerEvents: "none",
    });

    originals.push({ span, visibility: span.style.visibility });
    span.style.visibility = "hidden";
    root.appendChild(overlay);

    // Legacy overlay calibration retained for callers that explicitly use
    // this helper. Strict preview-parity captures now keep the source DOM and
    // calibrate only inside html2canvas's final clone.
    const captureTextTop = getLastTextRectTop(overlay);
    const rootTop = root.getBoundingClientRect().top;
    const overlayStyle = window.getComputedStyle(overlay);
    const captureCanvasBaseline = getHtml2CanvasAlphabeticBaseline(
      overlayStyle.fontFamily,
      overlayStyle.fontSize,
    );
    overlay.style.top = `${getRoleNameOverlayTop({
      initialTop: box.top,
      targetBaselineInRoot: snap.baselineInRoot,
      captureTextTopInRoot:
        captureTextTop == null ? null : captureTextTop - rootTop,
      html2CanvasBaseline: captureCanvasBaseline,
      fallbackShift: snap.canvasBaselineShift,
    })}px`;

    overlays.push(overlay);
  });

  return () => {
    overlays.forEach((overlay) => overlay.remove());
    originals.forEach(({ span, visibility }) => {
      span.style.visibility = visibility;
    });
    root.style.position = previousRootPosition;
  };
}

function getCaptureSize(element, bottomGuard = 2) {
  const root = element.getBoundingClientRect();
  let width = Math.max(element.offsetWidth, element.scrollWidth, 1);
  let height = Math.max(element.offsetHeight, element.scrollHeight, 1);

  element.querySelectorAll("*").forEach((child) => {
    const box = child.getBoundingClientRect();
    if (!box.width && !box.height) return;
    width = Math.max(width, box.right - root.left);
    height = Math.max(height, box.bottom - root.top);
  });

  // Glyph boxes catch descenders that element boxes can miss.
  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!String(node.nodeValue || "").trim()) continue;
    range.selectNodeContents(node);
    for (const box of range.getClientRects()) {
      if (!box.width && !box.height) continue;
      width = Math.max(width, box.right - root.left);
      height = Math.max(height, box.bottom - root.top);
    }
  }

  return {
    width: Math.max(1, Math.ceil(width)),
    height: Math.max(1, Math.ceil(height) + bottomGuard),
  };
}

/**
 * Keep the preview's exact block height unless the export clone genuinely
 * paints below it. Frozen line wrapping can make the last canvas glyph land
 * just outside the live DOM box; in that case, include a small paint guard so
 * html2canvas does not cut the final line in half.
 */
export function getOverflowSafeCaptureHeight({
  layoutHeight,
  measuredHeight,
  bottomGuard = 2,
}) {
  const layout = Math.max(1, Math.ceil(Number(layoutHeight) || 0));
  const measured = Math.max(1, Math.ceil(Number(measuredHeight) || 0));
  return measured > layout ? measured + bottomGuard : layout;
}

/**
 * Scroll blocks are stitched end-to-end, so their exported height must stay
 * identical to the preview layout. Capture overflow is useful for standalone
 * frames, but allowing it to grow a scroll PNG turns paint slack into visible
 * spacing between credits.
 */
export function getCaptureHeight({
  layoutHeight,
  measuredHeight,
  allowHeightOverflow = false,
  lockOutputHeight = false,
  bottomGuard = 2,
}) {
  const layout = Math.max(1, Math.ceil(Number(layoutHeight) || 0));
  if (!allowHeightOverflow || lockOutputHeight) return layout;
  return getOverflowSafeCaptureHeight({
    layoutHeight: layout,
    measuredHeight,
    bottomGuard,
  });
}

export function extractBackgroundImageUrls(backgroundImage) {
  const urls = [];
  const value = String(backgroundImage || "");
  const pattern = /url\((?:"([^"]*)"|'([^']*)'|([^)]*))\)/g;
  let match;

  while ((match = pattern.exec(value))) {
    const url = String(match[1] || match[2] || match[3] || "").trim();
    if (url) urls.push(url);
  }

  return urls;
}

function waitForDecodedImage(image, timeoutMs = 10000, strict = false) {
  const label = image.alt || "logo";
  const ensureLoaded = () => {
    if (image.naturalWidth > 0) return;
    if (strict) {
      throw new Error(`Unable to load ${label} for render capture`);
    }
  };
  const decode = async () => {
    if (typeof image.decode === "function") {
      try {
        await image.decode();
      } catch (error) {
        if (strict && image.naturalWidth <= 0) {
          throw new Error(`Unable to decode ${label} for render capture`);
        }
      }
    }
    ensureLoaded();
  };

  if (image.complete) return decode();

  return new Promise((resolve, reject) => {
    let timer;
    const cleanup = () => {
      clearTimeout(timer);
      image.removeEventListener?.("load", finish);
      image.removeEventListener?.("error", finish);
    };
    const finish = (event) => {
      cleanup();
      if (strict && event?.type === "error") {
        reject(new Error(`Unable to load ${label} for render capture`));
        return;
      }
      resolve();
    };
    timer = setTimeout(() => {
      cleanup();
      if (strict) {
        reject(new Error(`Timed out loading ${label} for render capture`));
        return;
      }
      finish();
    }, timeoutMs);
    image.addEventListener?.("load", finish, { once: true });
    image.addEventListener?.("error", finish, { once: true });
  }).then(decode);
}

export async function waitForCaptureAssets(root) {
  if (!root) return;

  const imageElements = [...root.querySelectorAll("img")];
  const logoImages = new Set(root.querySelectorAll(".logo_container img"));
  const backgroundNodes = [
    root,
    ...root.querySelectorAll("[style*='background'], .watermark"),
  ];
  const backgroundUrls = new Set();

  backgroundNodes.forEach((node) => {
    extractBackgroundImageUrls(getComputedStyle(node).backgroundImage).forEach(
      (url) => backgroundUrls.add(url),
    );
  });

  const preloaders = [...backgroundUrls].map((url) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = url;
    return image;
  });

  await Promise.all([
    ...imageElements.map((image) =>
      waitForDecodedImage(image, 10000, logoImages.has(image)),
    ),
    ...preloaders.map((image) => waitForDecodedImage(image)),
  ]);
}

/**
 * html2canvas mis-aligns nodes inside scrolled preview ancestors (phantom top
 * gap + clipped bottom). Clone into a fixed host at (0,0) so paint matches layout.
 */
function stripFocusOverlayClasses(root) {
  if (!root) return;
  const nodes = [
    root,
    ...root.querySelectorAll("[class*='focusDiv'], [class*='CardfocusDiv']"),
  ];
  nodes.forEach((el) => {
    if (!el?.classList) return;
    el.classList.remove("focusDiv", "CardfocusDiv");
    if (el.classList.contains("viewBlockfocusDiv")) {
      el.classList.remove("viewBlockfocusDiv");
      el.classList.add("viewBlock");
    }
    if (el.classList.contains("viewBlockCardfocusDiv")) {
      el.classList.remove("viewBlockCardfocusDiv");
      el.classList.add("viewBlock");
    }
  });
}

export function stripSearchHighlightsForCapture(root) {
  if (!root) return;

  root.querySelectorAll("mark.credit-search-hit").forEach((mark) => {
    const parent = mark.parentNode;
    mark.replaceWith(...mark.childNodes);
    parent?.normalize();
  });
}

export function mountIsolatedClone(element, fixedHeight = null) {
  const liveWidth = Math.max(element.offsetWidth, 1);
  const captureHeight = Number(fixedHeight);
  const hasFixedHeight = Number.isFinite(captureHeight) && captureHeight > 0;
  const host = document.createElement("div");
  host.setAttribute("data-render-capture-host", "true");
  host.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    "z-index:-1",
    "pointer-events:none",
    `width:${liveWidth}px`,
    hasFixedHeight ? `height:${captureHeight}px` : "",
    "margin:0",
    "padding:0",
    "border:none",
    "background:transparent",
  ].join(";");

  const clone = element.cloneNode(true);
  stripFocusOverlayClasses(clone);
  stripSearchHighlightsForCapture(clone);
  clone.style.width = `${liveWidth}px`;
  clone.style.maxWidth = `${liveWidth}px`;
  if (hasFixedHeight) {
    clone.style.height = `${captureHeight}px`;
    clone.style.minHeight = `${captureHeight}px`;
  }
  clone.style.transform = "none";
  clone.style.zoom = "1";
  clone.style.border = "none";
  clone.style.boxShadow = "none";
  clone.style.borderRadius = "0";
  clone.style.margin = "0";
  clone.style.position = "static";
  clone.style.left = "auto";
  clone.style.top = "auto";
  clone.style.overflow = "visible";

  host.appendChild(clone);

  // Empty heading/subheading wrappers can still paint a line-box in html2canvas.
  clone.querySelectorAll('[id^="heading_"], [id^="subheading_"]').forEach((el) => {
    if (!String(el.textContent || "").trim()) el.remove();
  });

  document.body.appendChild(host);
  void clone.offsetHeight;
  pinNameOnlyCaptureWidths(clone);
  pinRoleNameColumnWidths(clone, element);

  return {
    host,
    clone,
    dispose() {
      host.remove();
    },
  };
}

/** Percent name-only widths must stay at the preview px box during capture. */
function pinNameOnlyCaptureWidths(root) {
  root.querySelectorAll(".overlaytext, [id^='list']").forEach((el) => {
    const liveWidth = el.offsetWidth;
    if (liveWidth <= 0) return;
    el.style.boxSizing = "border-box";
    el.style.width = `${liveWidth}px`;
    el.style.maxWidth = `${liveWidth}px`;
  });
}

/**
 * Pin RoleName column boxes to the preview px width so html2canvas cannot
 * shrink them. Preserve the preview's table algorithm: gutter tables use
 * fixed 50/50 cells, while ragged tables intentionally size to content.
 */
function getUnscaledLayoutWidth(el) {
  const offsetWidth = Number(el?.offsetWidth);
  if (offsetWidth > 0) return offsetWidth;

  const rectWidth = Number(el?.getBoundingClientRect?.().width);
  return rectWidth > 0 ? rectWidth : 0;
}

function pinRoleNameColumnWidths(root, previewRoot = root) {
  const previewTables = [
    ...previewRoot.querySelectorAll("table.roleName_tr"),
  ];

  root.querySelectorAll("table.roleName_tr").forEach((table, tableIndex) => {
    // The isolated capture copy can reflow intrinsic ragged tables before it
    // is measured. Always take geometry and wrap state from the live preview;
    // otherwise a prematurely narrowed copy gets locked at the wrong width.
    const previewTable = previewTables[tableIndex] || table;
    const col = table.parentElement;
    const previewCol = previewTable.parentElement;
    if (col) {
      const colW = getUnscaledLayoutWidth(previewCol || col);
      if (colW > 0) {
        col.style.boxSizing = "border-box";
        col.style.flex = "none";
        col.style.width = `${colW}px`;
        col.style.maxWidth = `${colW}px`;
        col.style.minWidth = `${colW}px`;
      }
    }
    const tableW = getUnscaledLayoutWidth(previewTable);
    const previewWindow = previewTable.ownerDocument?.defaultView || window;
    const tableLayout = previewWindow.getComputedStyle(
      previewTable,
    ).tableLayout;
    const fixedLayout = tableLayout === "fixed";
    if (tableW > 0) {
      table.style.boxSizing = "border-box";
      table.style.width = `${tableW}px`;
      table.style.minWidth = `${tableW}px`;
      table.style.tableLayout = tableLayout;
    }
    const previewCells = [
      ...previewTable.querySelectorAll("td.roleName_td"),
    ];
    table.querySelectorAll("td.roleName_td").forEach((td, cellIndex) => {
      const previewTd = previewCells[cellIndex] || td;
      const tdW = getUnscaledLayoutWidth(previewTd);
      if (tdW <= 0) return;
      td.style.boxSizing = "border-box";
      if (!fixedLayout) {
        td.style.width = `${tdW}px`;
        td.style.minWidth = `${tdW}px`;
        td.style.maxWidth = `${tdW}px`;
      }
      td.style.overflow = "visible";
      td.style.wordBreak = "keep-all";
      td.style.overflowWrap = "normal";

      const span = [...td.children].find((child) => child.tagName === "SPAN");
      const previewSpan = [...previewTd.children].find(
        (child) => child.tagName === "SPAN",
      );
      if (
        span &&
        previewSpan &&
        String(previewSpan.textContent || "").trim() &&
        getWrappedTextLines(previewSpan).length <= 1
      ) {
        // A line that fits in preview must remain one line in the PNG. This
        // protects spaces and legal hyphen break points without globally
        // disabling wrapping for names that genuinely wrap in preview.
        span.style.whiteSpace = "nowrap";
        span.style.wordBreak = "keep-all";
        span.style.overflowWrap = "normal";
        span.style.wordWrap = "normal";
      }
    });
  });
}

/** Keep separator dimensions aligned to the final export pixel grid. */
export function getCenteredBulletCaptureGeometry({
  containerWidth,
  containerHeight,
  glyphWidth,
  glyphHeight,
  captureScale = 1,
}) {
  const scale = Number(captureScale) > 0 ? Number(captureScale) : 1;
  const width = Math.max(1 / scale, Math.round(glyphWidth * scale) / scale);
  const height = Math.max(1 / scale, Math.round(glyphHeight * scale) / scale);

  return {
    width,
    height,
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
  };
}

export function alignBulletsForCapture(root, captureScale = 1) {
  const bullets = [...root.querySelectorAll("span.bullet")];
  if (!bullets.length) return [];
  const rootRect = root.getBoundingClientRect();

  root
    .querySelectorAll(".capture-bullet-glyph")
    .forEach((captureGlyph) => captureGlyph.remove());
  root.querySelectorAll("[data-capture-bullet]").forEach((pair) => {
    [
      "data-capture-bullet",
      "data-capture-bullet-shape",
      "data-capture-bullet-color",
      "data-capture-bullet-width",
      "data-capture-bullet-height",
      "data-capture-bullet-center-x",
      "data-capture-bullet-center-y",
    ].forEach((attribute) => pair.removeAttribute(attribute));
  });

  // `rmNewClassbullets` is calculated by the interactive preview. The
  // isolated capture clone can wrap onto different rows after its final font
  // and width have settled, but React effects/ResizeObserver do not run in
  // that clone. Recalculate row endings from the clone's own geometry instead
  // of trusting those stale classes. This also prevents a stale visible
  // end-of-row bullet from being repainted far away from the credit names.
  const lineEndPairs = new Set();
  const pairs = [...root.querySelectorAll(".name-only-bullet-pair")];
  pairs.forEach((pair) => {
    const next = pair.nextElementSibling;
    const pairRect = pair.getBoundingClientRect();
    const nextRect =
      next?.classList?.contains("name-only-bullet-pair") &&
      next.parentElement === pair.parentElement
        ? next.getBoundingClientRect()
        : null;
    const lineSlop = Math.max(
      4,
      (pairRect.height || pair.offsetHeight || 0) * 0.45,
    );
    if (!nextRect || nextRect.top > pairRect.top + lineSlop) {
      lineEndPairs.add(pair);
    }
  });

  // Remove preview-only visibility before reading styles. Keep the separator
  // wrapper in normal flow (visibility preserves its exact layout space) so
  // the capture cannot change wrapping. Record its visual center relative to
  // the capture root before html2canvas clones the document; the final glyph
  // is painted geometrically at that stable point after capture.
  bullets.forEach((span) => span.classList.remove("rmNewClassbullets"));

  bullets.forEach((span) => {
    const pair =
      span.closest(".name-only-bullet-pair") || span.parentElement;
    const glyph = span.querySelector(".bullet-glyph");
    if (glyph) {
      const computed = window.getComputedStyle(glyph);
      const pairRect = pair?.getBoundingClientRect?.();
      const spanRect = span.getBoundingClientRect();
      const glyphWidth =
        parseFloat(computed.width) || glyph.getBoundingClientRect().width || 1;
      const glyphHeight =
        parseFloat(computed.height) || glyph.getBoundingClientRect().height || 1;
      const spanWidth = spanRect.width || parseFloat(span.style.width) || 1;
      const pairHeight = pairRect?.height || spanRect.height || 1;
      const geometry = getCenteredBulletCaptureGeometry({
        containerWidth: spanWidth,
        containerHeight: pairHeight,
        glyphWidth,
        glyphHeight,
        captureScale,
      });

      const hidden = lineEndPairs.has(pair);
      span.style.visibility = "hidden";
      glyph.style.visibility = "hidden";

      // Store root-relative coordinates now. Reading flex-child rectangles
      // from html2canvas's internal clone is unreliable, while these values
      // stay valid because visibility:hidden does not alter layout.
      if (!hidden && pair) {
        const shape = glyph.classList.contains("diamond")
          ? "diamond"
          : glyph.classList.contains("li-square")
            ? "square"
            : glyph.classList.contains("lineVertical")
              ? "lineVertical"
              : glyph.classList.contains("cross")
                ? "cross"
                : "circle";
        const centerX = spanRect.left - rootRect.left + spanWidth / 2;
        const centerY =
          (pairRect?.top ?? spanRect.top) - rootRect.top + pairHeight / 2;

        pair.setAttribute("data-capture-bullet", "true");
        pair.setAttribute("data-capture-bullet-shape", shape);
        pair.setAttribute(
          "data-capture-bullet-color",
          computed.color || getComputedStyle(span).color || "#000",
        );
        pair.setAttribute("data-capture-bullet-width", String(geometry.width));
        pair.setAttribute("data-capture-bullet-height", String(geometry.height));
        pair.setAttribute("data-capture-bullet-center-x", String(centerX));
        pair.setAttribute("data-capture-bullet-center-y", String(centerY));
      }
    }
  });

  return [];
}

/** Read stable root-relative separator centers from html2canvas's clone. */
export function collectBulletCaptureOverlays(root) {
  if (!root) return [];

  return [...root.querySelectorAll('[data-capture-bullet="true"]')]
    .map((pair) => {
      const width = parseFloat(pair.getAttribute("data-capture-bullet-width"));
      const height = parseFloat(pair.getAttribute("data-capture-bullet-height"));
      const centerX = parseFloat(
        pair.getAttribute("data-capture-bullet-center-x"),
      );
      const centerY = parseFloat(
        pair.getAttribute("data-capture-bullet-center-y"),
      );
      if (![width, height, centerX, centerY].every(Number.isFinite)) return null;

      return {
        shape: pair.getAttribute("data-capture-bullet-shape") || "circle",
        color: pair.getAttribute("data-capture-bullet-color") || "#000",
        width,
        height,
        left: centerX - width / 2,
        top: centerY - height / 2,
      };
    })
    .filter(Boolean);
}

/** Paint geometric separators at their measured final-clone centers. */
export function paintBulletCaptureOverlays(
  canvas,
  overlays,
  captureScale = 1,
) {
  if (!canvas || !overlays?.length) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const scale = Number(captureScale) > 0 ? Number(captureScale) : 1;

  overlays.forEach(({ shape, color, left, top, width, height }) => {
    const pixelWidth = Math.max(1, Math.round(width * scale));
    const pixelHeight = Math.max(1, Math.round(height * scale));
    const centerX = Math.round((left + width / 2) * scale);
    const centerY = Math.round((top + height / 2) * scale);

    ctx.save();
    // html2canvas leaves its capture-scale transform on the returned 2D
    // context. Coordinates below are already converted to canvas pixels, so
    // paint with the identity transform to avoid applying that scale twice.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.translate(centerX, centerY);

    if (shape === "circle") {
      ctx.beginPath();
      ctx.ellipse(0, 0, pixelWidth / 2, pixelHeight / 2, 0, 0, Math.PI * 2);
      ctx.fill();
    } else if (shape === "diamond") {
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-pixelWidth / 2, -pixelHeight / 2, pixelWidth, pixelHeight);
    } else if (shape === "cross") {
      ctx.lineWidth = Math.max(1, Math.round(pixelWidth * 0.18));
      ctx.lineCap = "butt";
      ctx.beginPath();
      ctx.moveTo(-pixelWidth / 2, -pixelHeight / 2);
      ctx.lineTo(pixelWidth / 2, pixelHeight / 2);
      ctx.moveTo(pixelWidth / 2, -pixelHeight / 2);
      ctx.lineTo(-pixelWidth / 2, pixelHeight / 2);
      ctx.stroke();
    } else {
      ctx.fillRect(-pixelWidth / 2, -pixelHeight / 2, pixelWidth, pixelHeight);
    }

    ctx.restore();
  });
}

const CAPTURE_SLACK_PX = 64;

function readViewBlockPads(clone) {
  const views = [
    ...clone.querySelectorAll(".viewBlock"),
    ...clone.querySelectorAll("[class*='viewBlock']"),
  ];
  const unique = [...new Set(views)];
  const padTop = unique.reduce((max, view) => {
    const inline = parseFloat(view.style.paddingTop);
    const computed = parseFloat(getComputedStyle(view).paddingTop);
    return Math.max(max, inline || 0, computed || 0);
  }, 0);
  const padBottom = unique.reduce((max, view) => {
    const inline = parseFloat(view.style.paddingBottom);
    const computed = parseFloat(getComputedStyle(view).paddingBottom);
    return Math.max(max, inline || 0, computed || 0);
  }, 0);
  return { views: unique, padTop, padBottom };
}

const HEADING_SELECTOR = '[id^="heading_"], [id^="subheading_"]';

function nextLayoutSection(element, root) {
  let branch = element;
  while (branch && branch !== root) {
    let sibling = branch.nextElementSibling;
    while (sibling) {
      const box = sibling.getBoundingClientRect();
      const hasContent =
        Boolean(String(sibling.textContent || "").trim()) ||
        Boolean(sibling.querySelector("img, svg, canvas, video"));
      // Empty heading wrappers are full-width but zero-height. Ignore their
      // width so the measured gap reaches the first actually rendered row.
      if (box.height > 0 || hasContent) return sibling;
      sibling = sibling.nextElementSibling;
    }
    branch = branch.parentElement;
  }
  return null;
}

function nextRenderedSibling(element, root = null) {
  let sibling = element?.nextElementSibling;
  while (sibling) {
    const box = sibling.getBoundingClientRect();
    const hasContent =
      Boolean(String(sibling.textContent || "").trim()) ||
      Boolean(sibling.querySelector("img, svg, canvas, video"));
    // Empty heading wrappers are full-width but zero-height. Ignore their
    // width so the measured gap reaches the first actually rendered content.
    if (box.height > 0 || hasContent) return sibling;
    sibling = sibling.nextElementSibling;
  }
  return nextLayoutSection(element, root);
}

/** Preserve the preview's heading box and its exact gap to following content. */
export function snapshotHeadingSpacing(root) {
  if (!root) return [];

  const rootBox = root.getBoundingClientRect();
  const layoutWidth = Number(root.offsetWidth);
  const visualScale =
    layoutWidth > 0 && rootBox.width > 0 ? rootBox.width / layoutWidth : 1;

  return [...root.querySelectorAll(HEADING_SELECTOR)]
    .filter((block) => String(block.textContent || "").trim())
    .map((block) => {
      const box = block.getBoundingClientRect();
      const next = nextRenderedSibling(block, root);
      const nextBox = next?.getBoundingClientRect();
      const computed = getComputedStyle(block);

      const lineElements = [...block.querySelectorAll("span")].filter((line) =>
        String(line.textContent || "").trim(),
      );
      const visibleLines = lineElements.length ? lineElements : [block];

      return {
        id: block.id,
        height: box.height / visualScale,
        gapAfter: nextBox ? (nextBox.top - box.bottom) / visualScale : null,
        lineHeight: computed.lineHeight,
        marginBottom: parseFloat(computed.marginBottom) || 0,
        lines: visibleLines.map((line) => {
          const lineBox = line.getBoundingClientRect();
          const lineStyle = getComputedStyle(line);
          const browserBaseline = getBrowserAlphabeticBaseline(line);
          const textTop = getLastTextRectTop(line);
          const html2CanvasBaseline = getHtml2CanvasAlphabeticBaseline(
            lineStyle.fontFamily,
            lineStyle.fontSize,
          );
          return {
            text: String(line.textContent || "").trim(),
            captureBox: {
              left: (lineBox.left - rootBox.left) / visualScale,
              top: (lineBox.top - rootBox.top) / visualScale,
              width: lineBox.width / visualScale,
              height: lineBox.height / visualScale,
            },
            baselineInRoot: (browserBaseline - rootBox.top) / visualScale,
            canvasBaselineShift: getCardRoleNameCanvasBaselineShift(
              (browserBaseline - lineBox.top) / visualScale,
              textTop == null ? null : (textTop - lineBox.top) / visualScale,
              html2CanvasBaseline,
            ),
          };
        }),
      };
    });
}

/** Paint heading/subheading lines at their exact browser preview baselines. */
export function overlayHeadingAtPreviewPositions(root, liveSnapshots = null) {
  if (!root || !liveSnapshots?.length) return () => {};

  const previousRootPosition = root.style.position;
  const originals = [];
  const overlays = [];
  root.style.position = "relative";

  liveSnapshots.forEach((snapshot) => {
    const block = root.querySelector(`#${snapshot.id}`);
    if (!block) return;
    const sourceLines = [...block.querySelectorAll("span")].filter((line) =>
      String(line.textContent || "").trim(),
    );
    const lines = sourceLines.length ? sourceLines : [block];

    lines.forEach((line, index) => {
      const live = snapshot.lines?.[index];
      const box = live?.captureBox;
      if (!live || !box) return;

      const computed = getComputedStyle(line);
      const overlay = document.createElement("span");
      overlay.textContent = live.text;
      ROLE_NAME_OVERLAY_STYLE_PROPS.forEach((prop) => {
        const value = computed.getPropertyValue(prop);
        if (value) overlay.style.setProperty(prop, value);
      });
      overlay.setAttribute("data-heading-overlay", "true");
      assignStyles(overlay, {
        position: "absolute",
        left: `${box.left}px`,
        top: `${box.top}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
        minWidth: "0",
        maxWidth: "none",
        margin: "0",
        padding: "0",
        border: "0",
        boxSizing: "border-box",
        display: "block",
        overflow: "visible",
        visibility: "visible",
        whiteSpace: "nowrap",
        wordBreak: "keep-all",
        overflowWrap: "normal",
        pointerEvents: "none",
      });

      originals.push({ line, visibility: line.style.visibility });
      line.style.visibility = "hidden";
      root.appendChild(overlay);

      const captureTextTop = getLastTextRectTop(overlay);
      const rootTop = root.getBoundingClientRect().top;
      const overlayStyle = getComputedStyle(overlay);
      const captureCanvasBaseline = getHtml2CanvasAlphabeticBaseline(
        overlayStyle.fontFamily,
        overlayStyle.fontSize,
      );
      overlay.style.top = `${getRoleNameOverlayTop({
        initialTop: box.top,
        targetBaselineInRoot: live.baselineInRoot,
        captureTextTopInRoot:
          captureTextTop == null ? null : captureTextTop - rootTop,
        html2CanvasBaseline: captureCanvasBaseline,
        fallbackShift: live.canvasBaselineShift,
      })}px`;
      overlays.push(overlay);
    });
  });

  return () => {
    overlays.forEach((overlay) => overlay.remove());
    originals.forEach(({ line, visibility }) => {
      line.style.visibility = visibility;
    });
    root.style.position = previousRootPosition;
  };
}

/** html2canvas drops unitless line-height. Pin heading/subheading to used px. */
export function bakeHeadingMetrics(root, liveSnapshots = null) {
  const snapshots = new Map(
    (liveSnapshots || []).map((snapshot) => [snapshot.id, snapshot]),
  );
  const blocks = [...root.querySelectorAll(HEADING_SELECTOR)];

  blocks.forEach((block) => {
    if (!String(block.textContent || "").trim()) return;
    [block, ...block.querySelectorAll("span")].forEach((el) => {
      const computed = getComputedStyle(el);
      const fontSize = parseFloat(computed.fontSize) || 0;
      const used = computed.lineHeight;
      if (used && used !== "normal") {
        el.style.lineHeight = used;
      } else if (fontSize) {
        const unitless = parseFloat(el.style.lineHeight);
        const multiplier = unitless > 0 && unitless < 10 ? unitless : 1;
        el.style.lineHeight = `${fontSize * multiplier}px`;
      }
      const marginBottom = parseFloat(computed.marginBottom);
      if (marginBottom) el.style.marginBottom = `${marginBottom}px`;
    });

    const live = snapshots.get(block.id);
    if (!live || !(live.height > 0)) return;
    block.style.boxSizing = "border-box";
    block.style.height = `${live.height}px`;
    block.style.minHeight = `${live.height}px`;
    block.style.flexShrink = "0";
    if (live.lineHeight && live.lineHeight !== "normal") {
      block.style.lineHeight = live.lineHeight;
    }
  });

  // Apply gaps after every heading box is pinned so adjacent heading and
  // subheading measurements cannot influence one another.
  blocks.forEach((block) => {
    const live = snapshots.get(block.id);
    if (!live || !String(block.textContent || "").trim()) return;

    const next = nextRenderedSibling(block, root);
    if (!next || !Number.isFinite(live.gapAfter)) {
      block.style.marginBottom = `${live.marginBottom}px`;
      return;
    }

    const box = block.getBoundingClientRect();
    const nextBox = next.getBoundingClientRect();
    const currentGap = nextBox.top - box.bottom;
    const currentMargin = parseFloat(getComputedStyle(block).marginBottom) || 0;
    const adjustedMargin = currentMargin + live.gapAfter - currentGap;
    block.style.marginBottom = `${adjustedMargin}px`;
  });
}

/** Line box of heading/subheading in canvas pixels (includes line-height). */
function headingLineBox(clone, canvas) {
  const root = clone.getBoundingClientRect();
  const scale = canvas.width / Math.max(1, clone.offsetWidth);
  let top = Infinity;
  let bottom = -Infinity;
  clone.querySelectorAll('[id^="heading_"], [id^="subheading_"]').forEach((el) => {
    if (!String(el.textContent || "").trim()) return;
    const box = el.getBoundingClientRect();
    top = Math.min(top, (box.top - root.top) * scale);
    bottom = Math.max(bottom, (box.bottom - root.top) * scale);
  });
  if (!Number.isFinite(top)) return null;
  return {
    top: Math.max(0, Math.floor(top)),
    bottom: Math.min(canvas.height - 1, Math.ceil(bottom)),
  };
}

/** Extra bottom lines are empty <br>/spacers — no ink, so crop would drop them. */
function endlinesBox(clone, canvas) {
  const root = clone.getBoundingClientRect();
  const scale = canvas.width / Math.max(1, clone.offsetWidth);
  let bottom = -Infinity;
  clone.querySelectorAll('[id$="endlines"]').forEach((el) => {
    if ((el.offsetHeight || 0) < 1) return;
    const box = el.getBoundingClientRect();
    bottom = Math.max(bottom, (box.bottom - root.top) * scale);
  });
  if (!Number.isFinite(bottom)) return null;
  return {
    bottom: Math.min(canvas.height - 1, Math.ceil(bottom)),
  };
}

/** Margin lives on .viewBlock. Strip it for capture so we can add it back exactly. */
function stripMarginAndAddSlack(clone) {
  const { views, padTop, padBottom } = readViewBlockPads(clone);
  views.forEach((view) => {
    view.style.paddingTop = "0px";
    view.style.paddingBottom = "0px";
  });
  clone.querySelectorAll("p").forEach((p) => {
    p.style.marginTop = "0";
    p.style.marginBottom = "0";
  });
  clone.querySelectorAll("*").forEach((el) => {
    el.style.overflow = "visible";
  });
  clone.style.overflow = "visible";
  clone.style.paddingTop = `${CAPTURE_SLACK_PX}px`;
  clone.style.paddingBottom = `${CAPTURE_SLACK_PX}px`;
  return { padTop, padBottom };
}

function parseCssColor(color) {
  if (!color || color === "transparent") return null;
  const value = String(color).trim();
  if (value.startsWith("#")) {
    const hex = value.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((ch) => ch + ch)
            .join("")
        : hex;
    const n = parseInt(full.slice(0, 6), 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;
  const parts = match[1].split(",").map((part) => parseFloat(part));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  if (parts[3] === 0) return null;
  return { r: parts[0], g: parts[1], b: parts[2] };
}

function verticalInkBounds(canvas, backgroundColor) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const bg = parseCssColor(backgroundColor);

  const isInk = (index) => {
    const alpha = data[index + 3];
    if (alpha < 16) return false;
    if (!bg) return true;
    const delta =
      Math.abs(data[index] - bg.r) +
      Math.abs(data[index + 1] - bg.g) +
      Math.abs(data[index + 2] - bg.b);
    return delta > 40;
  };

  const rowHasInk = (y) => {
    const row = y * width * 4;
    for (let x = 0; x < width; x += 2) {
      if (isInk(row + x * 4)) return true;
    }
    return false;
  };

  let top = 0;
  while (top < height && !rowHasInk(top)) top += 1;
  let bottom = height - 1;
  while (bottom > top && !rowHasInk(bottom)) bottom -= 1;
  // No painted pixels (empty logo slot). Caller must drop capture slack.
  if (top >= height || top >= bottom) return { top: 0, height, empty: true };
  const guard = 4;
  const croppedTop = Math.max(0, top - guard);
  const croppedBottom = Math.min(height - 1, bottom + guard);
  return { top: croppedTop, height: croppedBottom - croppedTop + 1, empty: false };
}

/** Content box only — excludes the temporary capture slack padding. */
function boundsWithoutSlack(clone, canvas) {
  const scale = canvas.width / Math.max(1, clone.offsetWidth);
  const slackTop = (parseFloat(clone.style.paddingTop) || 0) * scale;
  const slackBottom = (parseFloat(clone.style.paddingBottom) || 0) * scale;
  const top = Math.min(canvas.height - 1, Math.max(0, Math.round(slackTop)));
  const bottom = Math.max(top, Math.round(canvas.height - slackBottom) - 1);
  return { top, height: Math.max(1, bottom - top + 1), empty: false };
}

async function composeTrimmedPng({
  canvas,
  srcTop,
  srcHeight,
  destWidth,
  contentDestHeight,
  padTopDest,
  padBottomDest,
  backgroundColor,
}) {
  const destHeight = contentDestHeight + padTopDest + padBottomDest;
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");
  const bg = parseCssColor(backgroundColor);
  if (bg) {
    ctx.fillStyle = `rgb(${bg.r}, ${bg.g}, ${bg.b})`;
    ctx.fillRect(0, 0, destWidth, destHeight);
  } else {
    ctx.clearRect(0, 0, destWidth, destHeight);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    canvas,
    0,
    srcTop,
    canvas.width,
    srcHeight,
    0,
    padTopDest,
    destWidth,
    contentDestHeight,
  );
  const blob = await offscreen.convertToBlob({ type: "image/png" });
  return { blob, width: destWidth, height: destHeight };
}

/**
 * Captures a single DOM element as a sharp PNG Blob.
 * Pass targetWidth to scale the PNG to the export resolution without
 * reflowing the live preview (so wrapping stays identical).
 * Pass captureSize {width, height} to crop the capture to an exact box, and
 * targetHeight to pin the output height — needed when the consumer (e.g.
 * RenderX3 card frames) requires exact pixel dimensions, because measured
 * DOM extents can exceed the frame (overflowing content, sub-pixel ceil).
 */
export async function captureElementAsPng(element, options = {}) {
  const {
    scale = RENDER_SCALE,
    backgroundColor,
    backgroundStyle = null,
    targetWidth = null,
    targetHeight = null,
    captureSize = null,
    hideContent = false,
    allowHeightOverflow = false,
    lockOutputHeight = false,
    preserveRoleNamePositions = false,
    rasterizeAtTargetWidth = false,
    preserveParagraphPositions = false,
    preserveHeadingSpacing = false,
  } = options;

  await document.fonts.ready;
  await waitForCaptureAssets(element);

  // When preserving preview positions, keep the exact source DOM. Replacing
  // table and heading text with absolutely positioned copies changes their
  // line boxes before html2canvas sees them and creates a second baseline to
  // reconcile. The final-clone calibration below now fixes canvas painting
  // without changing layout. The legacy freeze remains for callers that do
  // not request strict preview parity.
  const liveRoleNameWraps = preserveRoleNamePositions
    ? null
    : snapshotRoleNameWraps(element);
  const liveParagraphWraps = preserveParagraphPositions
    ? snapshotFrozenTextWraps(element)
    : null;
  const liveHeadingSpacing = preserveHeadingSpacing
    ? snapshotHeadingSpacing(element)
    : null;

  const isolated = mountIsolatedClone(element, captureSize?.height);
  const { clone, dispose } = isolated;

  if (backgroundStyle) {
    applyBackgroundStyles(clone, backgroundStyle);
  }
  if (hideContent) {
    [...clone.children].forEach((child) => {
      child.style.visibility = "hidden";
    });
  }
  await waitForCaptureAssets(clone);

  const canvasBackgroundColor =
    backgroundColor !== undefined
      ? backgroundColor
      : backgroundStyle
        ? getHtml2CanvasBackgroundColor(backgroundStyle)
        : null;

  // Card frames pin an exact box. Scroll strips phantom html2canvas offset:
  // capture with slack, crop to ink, then restore margin_vertical as padding.
  const trimVertical = !captureSize && targetHeight == null;
  const marginPads = trimVertical
    ? stripMarginAndAddSlack(clone)
    : { padTop: 0, padBottom: 0 };

  const unfreeze = freezePreviewWrapping(clone, liveParagraphWraps);
  const unfreezeRoleName = preserveRoleNamePositions
    ? () => {}
    : freezeRoleNameToPreview(clone, liveRoleNameWraps);
  bakeHeadingMetrics(clone, liveHeadingSpacing);
  const unfreezeHeadings = () => {};

  try {
    void clone.offsetHeight;
    const layoutWidth = Math.max(1, clone.offsetWidth);
    let captureHeight;
    if (captureSize) {
      const measuredCaptureSize = getCaptureSize(clone, 0);
      captureHeight = getCaptureHeight({
        layoutHeight: captureSize.height,
        measuredHeight: measuredCaptureSize.height,
        allowHeightOverflow,
        lockOutputHeight,
      });
    } else {
      captureHeight = getCaptureSize(clone).height;
    }
    const captureWidth = captureSize?.width || layoutWidth;
    const captureScale = rasterizeAtTargetWidth
      ? getScrollTargetAlignedCaptureScale({
          captureWidth,
          targetWidth,
          fallbackScale: scale,
        })
      : preserveRoleNamePositions
        ? getCardTargetAlignedCaptureScale({
            captureHeight,
            targetHeight,
            fallbackScale: scale,
          })
        : scale;
    alignBulletsForCapture(clone, captureScale);
    void clone.offsetHeight;

    let bulletCaptureOverlays = [];
    const canvas = await html2canvas(clone, {
      backgroundColor: canvasBackgroundColor,
      scale: captureScale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      // Native layout, not per-glyph spans — per-letter rendering changes wrap.
      letterRendering: false,
      width: captureWidth,
      height: captureHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
      // This is the only DOM html2canvas actually parses. Calibrating here
      // avoids measuring an earlier clone and fixes baseline parity for every
      // text block type with the same rule.
      onclone: (_clonedDocument, clonedRoot) => {
        calibrateFinalCloneTextBaselines(clonedRoot);
        bulletCaptureOverlays = collectBulletCaptureOverlays(clonedRoot);
      },
    });
    paintBulletCaptureOverlays(
      canvas,
      bulletCaptureOverlays,
      captureScale,
    );
    const destWidth = targetWidth
      ? Math.round(targetWidth)
      : Math.round(canvas.width / captureScale);

    if (trimVertical) {
      let bounds = verticalInkBounds(canvas, canvasBackgroundColor);
      if (bounds.empty) {
        bounds = boundsWithoutSlack(clone, canvas);
      }
      const lineBox = headingLineBox(clone, canvas);
      if (lineBox) {
        const inkBottom = bounds.top + bounds.height - 1;
        const top = Math.max(0, Math.min(bounds.top, lineBox.top));
        const bottom = Math.min(
          canvas.height - 1,
          Math.max(inkBottom, lineBox.bottom),
        );
        bounds.top = top;
        bounds.height = Math.max(1, bottom - top + 1);
      }
      const extraBox = endlinesBox(clone, canvas);
      if (extraBox) {
        const inkBottom = bounds.top + bounds.height - 1;
        const bottom = Math.min(
          canvas.height - 1,
          Math.max(inkBottom, extraBox.bottom),
        );
        bounds.height = Math.max(1, bottom - bounds.top + 1);
      }
      const contentDestHeight = Math.max(
        1,
        Math.round(bounds.height * (destWidth / canvas.width)),
      );
      const pxPerCss = destWidth / Math.max(1, clone.offsetWidth);
      const composed = await composeTrimmedPng({
        canvas,
        srcTop: bounds.top,
        srcHeight: bounds.height,
        destWidth,
        contentDestHeight,
        padTopDest: Math.round(marginPads.padTop * pxPerCss),
        padBottomDest: Math.round(marginPads.padBottom * pxPerCss),
        backgroundColor: canvasBackgroundColor,
      });
      return {
        blob: composed.blob,
        width: composed.width,
        height: composed.height,
        cssWidth: composed.width,
        cssHeight: composed.height,
      };
    }

    const capturedDestHeight = Math.round(
      canvas.height * (destWidth / canvas.width),
    );
    const canGrowOutput = allowHeightOverflow && !lockOutputHeight;
    const destHeight = targetHeight && canGrowOutput
      ? Math.max(Math.round(targetHeight), capturedDestHeight)
      : targetHeight
        ? Math.round(targetHeight)
        : capturedDestHeight;
    const blob = await resizeCanvasToBlob(canvas, destWidth, destHeight);

    return {
      blob,
      width: destWidth,
      height: destHeight,
      cssWidth: destWidth,
      cssHeight: destHeight,
    };
  } finally {
    unfreezeHeadings();
    unfreezeRoleName();
    unfreeze();
    dispose();
  }
}

/**
 * Solid PNG at the export resolution. Used as the lead-in and lead-out
 * frames around captured credit blocks.
 */
export async function createBlankPng({ width, height, color = "transparent" }) {
  const destWidth = Math.max(1, Math.round(width));
  const destHeight = Math.max(1, Math.round(height));
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");
  const transparent =
    !color ||
    String(color).toLowerCase() === "transparent" ||
    String(color).toLowerCase() === "rgba(0, 0, 0, 0)";
  if (transparent) {
    ctx.clearRect(0, 0, destWidth, destHeight);
  } else {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, destWidth, destHeight);
  }
  const blob = await offscreen.convertToBlob({ type: "image/png" });
  return {
    blob,
    width: destWidth,
    height: destHeight,
    cssWidth: destWidth,
    cssHeight: destHeight,
  };
}

/**
 * Export lays out credits at previewWidth then applies transform:scale(video/preview)
 * on an inner wrapper. html2canvas mishandles that scale: bottoms clip and a
 * phantom top gap appears. Capture at unscaled layout size; targetWidth upscales.
 */
function withExportScaleDisabled(block, run) {
  const children = [...(block?.children || [])];
  const scaled = children.find((child) => {
    if (!(child instanceof HTMLElement)) return false;
    if (
      child.classList.contains("watermark") ||
      child.classList.contains("watermark_displaynone")
    ) {
      return false;
    }
    return /scale\s*\(/i.test(child.style.transform || "");
  });

  if (!scaled) return run();

  const prevTransform = scaled.style.transform;
  const prevBlockWidth = block.style.width;
  scaled.style.transform = "none";
  const contentWidth = Math.max(scaled.offsetWidth, 1);
  block.style.width = `${contentWidth}px`;

  const restore = () => {
    scaled.style.transform = prevTransform;
    block.style.width = prevBlockWidth;
  };

  try {
    const result = run();
    if (result && typeof result.then === "function") {
      return result.finally(restore);
    }
    restore();
    return result;
  } catch (error) {
    restore();
    throw error;
  }
}

/**
 * Captures every .blockWrapper element and returns ordered PNG blobs.
 */
export async function generateBlockImages(options = {}) {
  const {
    scale = RENDER_SCALE,
    onProgress,
    backgroundStyle = null,
    getBackgroundStyleForBlock = null,
    targetWidth = null,
    backgroundColor,
    preserveLayoutHeight = false,
    rasterizeAtTargetWidth = false,
    preserveRoleNamePositions = false,
    preserveHeadingSpacing = false,
  } = options;

  const blocks = document.querySelectorAll(".blockWrapper");
  if (!blocks.length) throw new Error("No .blockWrapper elements found");

  // The preview may be zoomed (fullscreen fit). Zoom must be OFF while
  // capturing: html2canvas and the rect-based measurements here assume
  // unzoomed layout pixels. Resetting zoom never reflows the credits —
  // it only changes display size — so the capture itself is unaffected.
  const zoomWrapper = document.getElementById("preview-zoom-wrapper");
  const previousZoom = zoomWrapper ? zoomWrapper.style.zoom : null;
  if (zoomWrapper) zoomWrapper.style.zoom = 1;

  const previewEl = document.getElementById("preview");
  const previousScrollTop = previewEl ? previewEl.scrollTop : 0;
  if (previewEl) previewEl.scrollTop = 0;

  try {
    const results = [];
    let offsetY = 0;
    let layoutBottom = 0;

    for (let idx = 0; idx < blocks.length; idx++) {
      const item = blocks[idx];
      const layoutContent = [...item.children].find(
        (child) =>
          child.nodeType === 1 &&
          !child.classList.contains("watermark") &&
          !child.classList.contains("watermark_displaynone"),
      );
      const layoutWidth = Math.max(1, layoutContent?.offsetWidth || item.offsetWidth);
      const layoutHeight = Math.max(
        item.offsetHeight,
        item.scrollHeight,
        layoutContent?.offsetHeight || 0,
      );
      const previousLayoutBottom = layoutBottom;
      layoutBottom += layoutHeight;
      const targetLayoutHeight =
        preserveLayoutHeight && targetWidth
          ? getCumulativeScaledBlockHeight({
              previousLayoutBottom,
              layoutBottom,
              layoutWidth,
              targetWidth,
            })
          : null;
      const blockBackgroundStyle = getBackgroundStyleForBlock
        ? getBackgroundStyleForBlock(item, idx, offsetY)
        : backgroundStyle;

      const { blob, width, height, cssWidth, cssHeight } =
        await withExportScaleDisabled(item, () =>
          captureElementAsPng(item, {
            scale,
            backgroundStyle: blockBackgroundStyle,
            targetWidth,
            targetHeight: targetLayoutHeight,
            captureSize: preserveLayoutHeight
              ? { width: layoutWidth, height: layoutHeight }
              : null,
            allowHeightOverflow: preserveLayoutHeight,
            lockOutputHeight: preserveLayoutHeight,
            preserveRoleNamePositions,
            rasterizeAtTargetWidth,
            preserveParagraphPositions: true,
            preserveHeadingSpacing,
            backgroundColor,
          }),
        );

      results.push({ blob, idx, width, height, cssWidth, cssHeight });
      onProgress?.(idx + 1, blocks.length);
      offsetY += item.offsetHeight;
    }

    return results;
  } finally {
    if (previewEl) previewEl.scrollTop = previousScrollTop;
    if (zoomWrapper) zoomWrapper.style.zoom = previousZoom ?? "";
  }
}

/**
 * Captures the final scroll block as two perfectly aligned layers:
 * a background-only replacement for the moving strip and a transparent
 * foreground that can continue moving, then remain pinned at center.
 */
export async function generateEndHoldAssets(options = {}) {
  const {
    scale = RENDER_SCALE,
    targetWidth,
    targetHeight,
    backgroundColor,
    getBackgroundStyleForBlock = null,
    rasterizeAtTargetWidth = false,
    preserveRoleNamePositions = false,
    preserveHeadingSpacing = false,
  } = options;
  const blocks = [...document.querySelectorAll(".blockWrapper")];
  const item = blocks[blocks.length - 1];
  if (!item) throw new Error("No final .blockWrapper element found");

  const zoomWrapper = document.getElementById("preview-zoom-wrapper");
  const previousZoom = zoomWrapper ? zoomWrapper.style.zoom : null;
  if (zoomWrapper) zoomWrapper.style.zoom = 1;

  const previewEl = document.getElementById("preview");
  const previousScrollTop = previewEl ? previewEl.scrollTop : 0;
  if (previewEl) previewEl.scrollTop = 0;

  try {
    const layoutContent = [...item.children].find(
      (child) =>
        child.nodeType === 1 &&
        !child.classList.contains("watermark") &&
        !child.classList.contains("watermark_displaynone"),
    );
    const layoutWidth = Math.max(
      1,
      layoutContent?.offsetWidth || item.offsetWidth,
    );
    const layoutHeight = Math.max(
      item.offsetHeight,
      item.scrollHeight,
      layoutContent?.offsetHeight || 0,
    );
    const offsetY = blocks
      .slice(0, -1)
      .reduce((sum, block) => sum + block.offsetHeight, 0);
    const backgroundStyle = getBackgroundStyleForBlock
      ? getBackgroundStyleForBlock(item, blocks.length - 1, offsetY)
      : null;
    const shared = {
      scale,
      targetWidth,
      targetHeight,
      captureSize: { width: layoutWidth, height: layoutHeight },
      allowHeightOverflow: true,
      lockOutputHeight: true,
      preserveRoleNamePositions,
      rasterizeAtTargetWidth,
      preserveParagraphPositions: true,
      preserveHeadingSpacing,
    };

    const background = await withExportScaleDisabled(item, () =>
      captureElementAsPng(item, {
        ...shared,
        backgroundColor,
        backgroundStyle,
        hideContent: true,
      }),
    );
    const foreground = await withExportScaleDisabled(item, () =>
      captureElementAsPng(item, {
        ...shared,
        backgroundColor: null,
        backgroundStyle: null,
      }),
    );

    return { background, foreground };
  } finally {
    if (previewEl) previewEl.scrollTop = previousScrollTop;
    if (zoomWrapper) zoomWrapper.style.zoom = previousZoom ?? "";
  }
}

/**
 * Stitches multiple PNG blobs into a single vertical strip.
 */
export async function generateStitchedImage(blockImages) {
  const bitmaps = await Promise.all(
    blockImages.map(({ blob }) => createImageBitmap(blob)),
  );

  const totalWidth = Math.max(...bitmaps.map((b) => b.width));
  const totalHeight = bitmaps.reduce((sum, b) => sum + b.height, 0);

  const offscreen = new OffscreenCanvas(totalWidth, totalHeight);
  const ctx = offscreen.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, totalWidth, totalHeight);

  let y = 0;
  for (const bitmap of bitmaps) {
    ctx.drawImage(bitmap, 0, y);
    y += bitmap.height;
    bitmap.close();
  }

  return offscreen.convertToBlob({ type: "image/png" });
}

export async function generateScrollPng(options = {}) {
  const blockImages = await generateBlockImages(options);
  return generateStitchedImage(blockImages);
}

